import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Package } from "lucide-react";
import { Mission } from "@/services/clientDataService";
import { useToast } from "@/hooks/use-toast";
import { usePaymentModalityState } from "@/hooks/usePaymentModalityState";
import { PaymentModalityHeader } from "@/components/payment-modality/PaymentModalityHeader";
import { PaymentModalitySlider } from "@/components/payment-modality/PaymentModalitySlider";
import { PaymentModalitySummary } from "@/components/payment-modality/PaymentModalitySummary";
import { PaymentModalityActions } from "@/components/payment-modality/PaymentModalityActions";
import type { PackAilixConfig } from "@/utils/packAilixPricing";

interface PaymentModalitySectionProps {
  selectedMissions: Mission[];
  totalCost: number;
  clientId: string;
  pricingMode?: "a_la_carte" | "pack_ailix_1an" | "pack_ailix_2ans";
  packConfig?: PackAilixConfig | null;
  clientTjm?: number;
  clientTjmMoe?: number;
}

const PaymentModalitySection: React.FC<PaymentModalitySectionProps> = ({
  selectedMissions,
  totalCost,
  clientId,
  pricingMode = "a_la_carte",
  packConfig,
  clientTjm,
  clientTjmMoe,
}) => {
  const { toast } = useToast();

  const {
    shouldRender,
    state,
    defaultData,
    sliderStep,
    handleDevelopersChange,
    handleSave,
    handleRecommended,
  } = usePaymentModalityState({
    selectedMissions,
    totalCost,
    clientId,
    pricingMode,
    packConfig,
    clientTjm,
    clientTjmMoe,
    toast,
  });

  if (!shouldRender) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h3 className="font-medium text-primary flex items-center gap-2">
        <Package className="w-4 h-4" />
        Configuration de l'équipe
      </h3>

      <Card className="border border-border shadow-sm hover:shadow-md transition-all duration-200">
        <PaymentModalityHeader
          missionCount={Array.isArray(selectedMissions) ? selectedMissions.length : 0}
          totalDays={typeof defaultData.totalDays === "number" ? defaultData.totalDays : 0}
        />

        <CardContent className="space-y-4">
          <PaymentModalitySlider
            developers={state.developers}
            isCustom={state.isCustom}
            pricingMode={pricingMode}
            sliderStep={sliderStep}
            monthlyCost={state.comparisonMonthlyCost ?? state.monthlyCost}
            onDevelopersChange={handleDevelopersChange}
          />

          <Separator />

          <PaymentModalitySummary
            developers={state.developers}
            projectMonths={state.projectMonths}
            pricingMode={pricingMode}
            packConfig={packConfig}
          />

          <Separator />

          <PaymentModalityActions
            isSaving={state.isSaving}
            lastSaved={state.lastSaved}
            onSave={handleSave}
            onRecommended={handleRecommended}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentModalitySection;
